/**
 * Base.
 */
import { make } from "vuex-pathify";
import _ from "lodash";
import api from "@/api";

export default class {
  constructor(namespace, endPoint, propsToClean) {
    this._namespaced = true;

    this._state = {
      entities: [],
      ogEntities: [],
      loadedEntity: {},
      loadedOgEntity: {},
      errorsOccurred: false,
      namespace: namespace,
      endPoint: endPoint
    };

    this._getters = {
      ...make.getters(this.state),
      getEntity(state, index) {
        return state.entities[index];
      }
    };

    this._mutations = {
      ...make.mutations(this.state),
      setErrorsOccurred(state, value) {
        state.errorsOccurred = value;
      },
      setEntities(state, entities) {
        state.entities = entities;
        state.ogEntities = this.copy(`${namespace}/entities`);
      },
      addEntity(state, newEntity) {
        var foundIndex = state.entities.findIndex(x => {
          return x.id === newEntity.id;
        });
        if (foundIndex < 0) {
          state.entities.unshift(newEntity);
          state.ogEntities.unshift(_.cloneDeep(newEntity));
        }
      },
      setLoadedEntity(state, entity) {
        state.loadedEntity = entity;
        state.loadedOgEntity = this.copy(`${namespace}/loadedEntity`);
      },
      deleteEntity(state, entityId) {
        state.entities = state.entities.filter(x => x.id !== entityId);
        state.ogEntities = state.ogEntities.filter(x => x.id !== entityId);
      },
      updateEntity(state, entity) {
        var foundIndex = state.entities.findIndex(x => {
          return x.id === entity.id;
        });
        if (foundIndex < 0) {
          return; // Nothing to update ... should be impossible.
        }

        var ogEntityIndex = state.ogEntities.findIndex(x => {
          return x.id === entity.id;
        });
        if (ogEntityIndex < 0) {
          return; // Nothing to update ... should be impossible.
        }

        state.entities.splice(foundIndex, 1, _.cloneDeep(entity)); // Clean original. - Reactive way of doing array change in Vue2
        state.ogEntities.splice(ogEntityIndex, 1, _.cloneDeep(entity)); // clean OG - Reactive way of doing array change in Vue2
        // state.entities[foundIndex] = _.cloneDeep(entity); // Clean original.
        // state.ogEntities[ogEntityIndex] = _.cloneDeep(entity); // clean OG
      },
      //find and replace the old entity with the new one
      replaceEntity(state, entity) {
        let entities = state.entities;
        let indexToReplace = entities.findIndex((r) => r.id === entity.id);
        entities.splice(indexToReplace, 1, entity); //Reactive way to change arrays; Note: this should technically be done in a mutation in the store because vue devtools debugger won't see the change
      },
      rollbackEntity(state, entity) {
        var foundIndex = state.entities.findIndex(x => {
          return x.id === entity.id;
        });
        if (foundIndex < 0) {
          return; // Nothing to rollback ... should be impossible.
        }

        var ogEntityIndex = state.ogEntities.findIndex(x => {
          return x.id === entity.id;
        });
        if (ogEntityIndex < 0) {
          return; // Nothing to rollback ... should be impossible.
        }

        state.entities[foundIndex] = _.cloneDeep(
          state.ogEntities[ogEntityIndex]
        ); // rollback
      }
    };

    this._actions = {
      loadEntities({ commit }, [queryParams, usePushError = true] = []) {
        // TODO: This is definitely not the way it should be done, we will change it when we have time. Don't copy
        if (!Array.isArray(queryParams)) {
          queryParams = arguments[1]; // If queryParams is passed as 1d array without the usePushError argument
        }
        var me = this;
        return new Promise(function(resolve, reject) {
          api
            .getEntity(me.state[namespace].endPoint, null, queryParams)
            .then(entities => {
              commit("setEntities", entities);
              resolve();
            })
            .catch(ex => {
              if (usePushError) {
                commit(
                  "pushError",
                  ex.msg || ex.value || "Failed to load Entities.",
                  {
                    root: true
                  }
                );
              }
              reject(ex);
            });
        });
      },
      loadEntity({ commit }, defaultEntity) {
        commit("setErrorsOccurred", false);
        // Check our cache. We will not reload the entity if we already have it.
        // If they came from the listing we will have it already.
        // The only way we don't have it is if they got to this page without coming through the listing.
        var found = this.state[namespace].entities.find(x => {
          return x.id == defaultEntity.id;
        });

        if (found) {
          commit("setLoadedEntity", found);
          return; // use cached
        } else {
          // Add a default instance of the entity now so that Vue's binding works as expected.
          // When the GET succeeds we will just update the entity as it already exists.
          commit("addEntity", defaultEntity);
          commit("setLoadedEntity", defaultEntity);
        }

        api
          .getEntity(this.state[namespace].endPoint, defaultEntity.id)
          .then(entity => {
            commit("updateEntity", entity);
            commit("setLoadedEntity", entity);
          })
          .catch(ex => {
            commit("setErrorsOccurred", true);
            commit(
              "pushError",
              ex.msg || ex.value || `Failed to load Entity.`,
              {
                root: true
              }
            );
          });
      },
      createEntity({ commit }, newEntity) {
        api
          .setEntity(this.state[namespace].endPoint, newEntity)
          .then(entity => commit("addEntity", entity))
          .catch(ex => {
            commit(
              "pushError",
              ex.msg || ex.value || `Failed to create Entity.`,
              {
                root: true
              }
            );
          });
      },
      updateEntity({ commit }, entity) {
        var me = this;
        commit("setErrorsOccurred", false);
        return new Promise(function(resolve, reject) {
          api
            .setEntity(me.state[namespace].endPoint, entity, propsToClean)
            .then(() => {
              commit("updateEntity", entity);
              resolve();
            })
            .catch(ex => {
              commit("setErrorsOccurred", true);
              commit("rollbackEntity", entity);
              commit(
                "pushError",
                ex.msg || ex.value || `Failed to update Entity.`,
                {
                  root: true
                }
              );
              reject();
            });
        });
      },
      deleteEntity({ commit }, id) {
        var me = this;
        commit("setErrorsOccurred", false);
        return new Promise(function(resolve) {
          api
            .delete(me.state[namespace].endPoint, id)
            .then(() => {
              commit("deleteEntity", id);
              resolve();
            })
            .catch(function(ex) {
              commit("setErrorsOccurred", true);
              commit(
                "pushError",
                ex.msg || ex.value || "Failed to delete Entity.",
                {
                  root: true
                }
              );
            });
        });
      }
    };
  }

  get namespaced() {
    return this._namespaced;
  }
  get state() {
    return this._state;
  }
  get getters() {
    return this._getters;
  }
  get mutations() {
    return this._mutations;
  }
  get actions() {
    return this._actions;
  }
  set namespaced(n) {
    this._namespaced = n;
  }
  set state(s) {
    this._state = s;
  }
  set getters(g) {
    this._getters = g;
  }
  set mutations(m) {
    this._mutations = m;
  }
  set actions(a) {
    this._actions = a;
  }
}
